import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "react-query";
import { recruitingAPI } from "../services/recruitingApi";
import { APPLICATION_STATUS_CONFIG } from "./StatusControl/configs/applicationConfig";

interface DropdownApplication {
  id: number;
  job_title: string;
  status: string;
  status_changed_at?: string;
}

interface AppCountDropdownProps {
  candidateId: number;
  count: number;
  mostAdvancedStatus?: string;
  /** Pre-fetched applications — skips lazy fetch when provided */
  applications?: DropdownApplication[];
}

const AppCountDropdown: React.FC<AppCountDropdownProps> = ({
  candidateId,
  count,
  mostAdvancedStatus,
  applications: prefetched,
}) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const { data: fetchedApps, isLoading } = useQuery(
    ["candidate-applications-dropdown", candidateId],
    () => recruitingAPI.getApplications({ candidate_id: candidateId }),
    {
      enabled: open && !prefetched,
      staleTime: 30_000,
    },
  );

  const allApps = (prefetched || fetchedApps || []) as DropdownApplication[];
  const applications = allApps.filter((app) => app.status !== "rejected");

  // Close on click-outside
  const handleClickOutside = useCallback((e: MouseEvent) => {
    if (
      containerRef.current &&
      !containerRef.current.contains(e.target as Node)
    ) {
      setOpen(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [open, handleClickOutside]);

  if (count <= 0) return null;

  const config = mostAdvancedStatus
    ? APPLICATION_STATUS_CONFIG[mostAdvancedStatus]
    : undefined;

  const bgColor = config?.bgColor || "#f3f4f6";
  const color = config?.color || "#374151";
  const borderColor = config?.color || "#d1d5db";

  return (
    <div
      ref={containerRef}
      style={{ position: "relative", display: "inline-block" }}
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((prev) => !prev);
        }}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "4px",
          padding: "2px 8px",
          borderRadius: "9999px",
          fontSize: "11px",
          fontWeight: 500,
          backgroundColor: bgColor,
          color: color,
          border: `1px solid ${borderColor}`,
          cursor: "pointer",
        }}
        title={`${count} application(s)`}
      >
        {count} app{count === 1 ? "" : "s"}
        {mostAdvancedStatus && config && (
          <>
            {" · "}
            {config.label}
          </>
        )}
      </button>

      {open && (
        <div
          role="presentation"
          onMouseDown={(e) => e.stopPropagation()}
          style={{
            position: "absolute",
            top: "100%",
            right: 0,
            zIndex: 200,
            backgroundColor: "white",
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            padding: "8px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            minWidth: "280px",
            marginTop: "4px",
          }}
        >
          {isLoading && !prefetched ? (
            <div
              style={{
                padding: "12px",
                textAlign: "center",
                fontSize: "12px",
                color: "#9ca3af",
              }}
            >
              Loading...
            </div>
          ) : applications.length === 0 ? (
            <div
              style={{
                padding: "12px",
                textAlign: "center",
                fontSize: "12px",
                color: "#9ca3af",
              }}
            >
              No applications found
            </div>
          ) : (
            applications.map((app) => {
              const appConfig = APPLICATION_STATUS_CONFIG[app.status];
              const daysInStatus = app.status_changed_at
                ? Math.max(
                    0,
                    Math.floor(
                      (Date.now() - new Date(app.status_changed_at).getTime()) /
                        (1000 * 60 * 60 * 24),
                    ),
                  )
                : null;
              return (
                <button
                  key={app.id}
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    navigate(`/applications/${app.id}`);
                  }}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "6px 8px",
                    borderRadius: "4px",
                    cursor: "pointer",
                    fontSize: "12px",
                    width: "100%",
                    background: "none",
                    border: "none",
                    fontFamily: "inherit",
                    textAlign: "left",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = "#f9fafb")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = "transparent")
                  }
                >
                  <span style={{ fontWeight: 500 }}>{app.job_title}</span>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <span
                      style={{
                        padding: "1px 6px",
                        borderRadius: "4px",
                        backgroundColor: appConfig?.bgColor || "#f3f4f6",
                        color: appConfig?.color || "#374151",
                        fontSize: "10px",
                        fontWeight: 500,
                      }}
                    >
                      {appConfig?.label || app.status}
                    </span>
                    {daysInStatus !== null && (
                      <span style={{ color: "#9ca3af", fontSize: "10px" }}>
                        {daysInStatus}d
                      </span>
                    )}
                  </div>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default AppCountDropdown;
